<template>
  <div class="position-absolute bg-white z-2 w-100 main-menu overflow-hidden">
    <div class="container position-relative d-flex flex-column h-100">
      <button
        class="btn-close position-absolute top-0 end-0 mx-3 my-4 p-2 rounded-circle border bg-white d-none d-lg-block"
        type="button"
        @click="close"
      />
      <a
        v-if="is_barcode"
        class="display-4 mt-4 text-decoration-none"
        href="#"
        @click="submit_barcode"
      >
        <icon name="BARCODE" />
        {{ barcode_string }}
      </a>
      <div
        v-else-if="loading.public_pages || is_loading"
        class="container text-center"
      >
        <div class="spinner-border muted m-5 p-5"></div>
      </div>
      <div v-else class="row h-100">
        <div
          class="col order-lg-last overflow-scroll overscroll-contain no-scrollbar h-100"
        >
          <results :pages="results_data" />
        </div>
        <div
          class="col-12 col-lg-3 overflow-scroll overscroll-contain no-scrollbar h-100 border-end g-0 me-2"
        >
          <filters
            :action="searchtab"
            class="mt-4"
            :filters="filters"
            :selected="filter"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import barcode_regex from "@pdfking/constants/barcode"
import {
  contains,
  filter,
  length,
  merge,
  object_map,
  ordered_object,
  scan,
} from "@pdfking/rfunc"
import { Values, SortBy } from "@pdfking/tfunc"
import icon from "@pdfking/workflow/components/icon"
import fire_event from "@pdfking/workflow/utils/fire_event"
import make_translations from "@pdfking/workflow/utils/make_translations"
import proxy from "@pdfking/workflow/utils/proxy"
import { url_query_encode } from "@pdfking/workflow/utils/urlutilities"
import set_property from "@pdfking/workflow/vue/3/set_property"

import autocomplete from "../../constants/autocomplete"
import close from "../../mixins/close"
import freeze from "../../mixins/freeze"
import search from "../../mixins/search"
import change_window_location from "../../navigation/change_window_location"
import state from "../../state/main"
import user from "../../state/user"
import cleanup from "../../utils/barcode_cleanup"
import get_django_url from "../../utils/get_django_url"
import is_firefox from "../../utils/is_firefox"
import navigate from "../navigate"
import { SEARCH_VIEW } from "../urls"
import filters from "./layout/filters.vue"
import results from "./layout/results.vue"

export default {
  components: { filters, icon, results },
  computed: merge(
    proxy(autocomplete, {
      loading: "loading",
      pages_index: "all_options",
      tags_index: "tags",
    }),

    {
      barcode_string() {
        return cleanup(this.query)
      },
      can_use_barcode() {
        return user.is_staff
      },
      default_results() {
        return user.is_staff ? Values(this.all_options || {}) : []
      },

      filtered_results() {
        return object_map(
          o => o.id,
          o => o,
          SortBy(
            filter(r => r.in_search, Values(this.results)),
            r => [r.kind.sequence, r.score]
          )
        )
      },
      filters() {
        let filters = []

        // Add "All" filter if there are multiple groups
        if (length(this.grouped_results) > 1) {
          filters.push({
            badge: length(this.filtered_results),
            icon: "DASHBOARD",
            id: null,
            title: this.translations.actions.ALL,
          })
        }

        // Add individual group filters
        for (const [group, inner] of Object.entries(this.grouped_results)) {
          filters.push({
            badge: length(inner),
            icon: this.tags[group].icon,
            id: group,
            title: this.tags[group].name,
          })
        }

        return filters
      },
      grouped_results() {
        let results = ordered_object()

        scan(
          r =>
            scan(t => {
              if (!results[t.id]) {
                results[t.id] = [r]
              } else {
                results[t.id].push(r)
              }
            }, r.tags),
          this.filtered_results
        )

        return results
      },
      is_barcode() {
        if (this.can_use_barcode && this.query) {
          const bits = filter(
            s => barcode_regex.barcode.test(s),
            this.query.split(barcode_regex.divider)
          )
          return length(bits) > 0
        }
        return false
      },
      results_data() {
        return this.filter
          ? this.grouped_results[this.filter] || []
          : this.filtered_results
      },
      translations: () => make_translations("actions"),
    }
  ),
  data() {
    return {
      expand_size: 10,

      expanded: {},
      tag: null,
      toggled_class: "search-body",
    }
  },

  methods: {
    contains,
    is_expanded(id) {
      return this.expanded[id]
    },
    is_firefox,
    length,
    searchtab(selected) {
      // Update URL with filter parameter
      const params = { ...this.$route.query }
      if (selected) {
        params.filter = selected
      } else {
        delete params.filter
      }
      navigate(SEARCH_VIEW, params)

      state.track_event("search_tab", "search")
    },
    submit_barcode() {
      if (this.is_barcode) {
        const qs = url_query_encode({ barcode: this.barcode_string })

        get_django_url("admin_barcode").then(url =>
          change_window_location(url + "?" + qs)
        )

        this.close()
      }
    },
    toggle(info) {
      state.track_event("search_link", "search")
      change_window_location(info.url)
      this.close()
    },
    toggle_expanded(id) {
      state.track_event("search_expand", "search")

      set_property(this.expanded, id, !this.expanded[id])
    },
  },

  mixins: [close, search, freeze],

  mounted() {
    fire_event(document, "focus_search")
  },

  props: ["query", "send", "filter"],

  watch: {
    send(v) {
      this.submit_barcode()
    },
  },
}
</script>
